import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content?: React.ReactNode;
  label?: string;
  className?: string;
}

interface TooltipViewport {
  target: HTMLElement;
  win: Window | null;
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Resolves the top-most (parent) window + document so the tooltip can render
 * outside the current browsing context (i.e. escape an iframe boundary).
 * It also accumulates the frame offsets needed to translate coordinates from
 * the current window's viewport to the top window's viewport.
 */
function getTooltipViewport(): TooltipViewport {
  let win: Window = window;
  let left = 0;
  let top = 0;

  try {
    while (win.parent && win.parent !== win) {
      const frame = win.frameElement as HTMLElement | null;
      if (frame) {
        const rect = frame.getBoundingClientRect();
        left += rect.left;
        top += rect.top;
      }
      win = win.parent;
    }
  } catch {
    win = window;
    left = 0;
    top = 0;
  }

  return {
    target: win.document.body,
    win: win === window ? null : win,
    left,
    top,
    width: win.innerWidth,
    height: win.innerHeight,
  };
}

const GAP = 6;
const EDGE_PADDING = 8;
const MAX_WIDTH = 320;
const FALLBACK_WIDTH = 280;
const FALLBACK_HEIGHT = 90;

let tooltipIdCounter = 0;

const CONTENT_BASE_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  transform: 'translateX(-50%)',
  zIndex: 999999,
  background: '#ffffff',
  color: '#000000',
  fontSize: 12,
  lineHeight: 1.4,
  padding: '6px 8px',
  border: '1px solid #000000',
  maxWidth: `min(${MAX_WIDTH}px, 90vw)`,
  width: `min(${FALLBACK_WIDTH}px, 90vw)`,
};

const Tooltip: React.FC<TooltipProps> = ({
  content = 'Lorem ipsum example',
  label = 'More information',
  className = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipIdRef = useRef<string>();
  if (!tooltipIdRef.current) {
    tooltipIdRef.current = `tooltip-${++tooltipIdCounter}`;
  }

  const showTooltip = isHovered || isClicked;

  // Touch/pointer devices synthesise mouseenter/mouseleave from taps, which can
  // leave isHovered stuck true and make the toggle feel like it "doesn't work".
  // Only treat the tooltip as "hovered" for real mouse pointers; taps rely on
  // the click toggle instead.
  const handlePointerEnter = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse') {
      setIsHovered(true);
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Close the tooltip when tapping/clicking anywhere outside of it.
  useEffect(() => {
    if (!showTooltip) return;
    const handleOutside = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;
      const inTrigger = triggerRef.current?.contains(target);
      const inContent = contentRef.current?.contains(target);
      if (inTrigger || inContent) return;
      setIsClicked(false);
      setIsHovered(false);
    };

    const viewport = getTooltipViewport();
    const targetDoc = viewport.target.ownerDocument;
    const docs: Document[] = [document];
    if (targetDoc !== document) docs.push(targetDoc);

    docs.forEach((doc) => {
      doc.addEventListener('pointerdown', handleOutside as EventListener, true);
      doc.addEventListener('mousedown', handleOutside as EventListener, true);
      doc.addEventListener('click', handleOutside as EventListener, true);
    });

    // Also listen on window(s) to catch events that don't bubble to document
    // across iframe boundaries (e.g. click in inner window when portal is in top).
    const wins: Window[] = [window];
    if (viewport.win && viewport.win !== window) wins.push(viewport.win);
    wins.forEach((win) => {
      win.addEventListener('pointerdown', handleOutside as EventListener, true);
    });

    return () => {
      docs.forEach((doc) => {
        doc.removeEventListener('pointerdown', handleOutside as EventListener, true);
        doc.removeEventListener('mousedown', handleOutside as EventListener, true);
        doc.removeEventListener('click', handleOutside as EventListener, true);
      });
      wins.forEach((win) => {
        win.removeEventListener('pointerdown', handleOutside as EventListener, true);
      });
    };
  }, [showTooltip]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const viewport = getTooltipViewport();
    const rect = trigger.getBoundingClientRect();

    const content = contentRef.current;
    const tooltipWidth = content?.offsetWidth ?? FALLBACK_WIDTH;
    const tooltipHeight = content?.offsetHeight ?? FALLBACK_HEIGHT;

    const halfWidth = tooltipWidth / 2;

    const anchorLeft = viewport.left + rect.left + rect.width / 2;
    const anchorTop = viewport.top + rect.bottom + GAP;

    const minLeft = EDGE_PADDING + halfWidth;
    const maxLeft = viewport.width - EDGE_PADDING - halfWidth;
    const left = Math.min(Math.max(anchorLeft, minLeft), Math.max(minLeft, maxLeft));

    let top = anchorTop;
    if (anchorTop + tooltipHeight > viewport.height - EDGE_PADDING) {
      top = viewport.top + rect.top - tooltipHeight - GAP;
    }

    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!showTooltip) return;
    const vp = getTooltipViewport();
    updatePosition();

    const targets: Window[] = [window, ...(vp.win ? [vp.win] : [])];
    targets.forEach((targetWindow) => {
      targetWindow.addEventListener('resize', updatePosition);
      targetWindow.addEventListener('scroll', updatePosition, true);
    });

    return () => {
      targets.forEach((targetWindow) => {
        targetWindow.removeEventListener('resize', updatePosition);
        targetWindow.removeEventListener('scroll', updatePosition, true);
      });
    };
  }, [showTooltip, updatePosition]);

  return (
    <span className={`tooltip ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className="tooltip__trigger"
        aria-label={label}
        aria-expanded={showTooltip}
        aria-describedby={showTooltip ? tooltipIdRef.current : undefined}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onClick={() => setIsClicked((value) => !value)}
      >
        <i className="mz-svg mz-svg--grey mz-svg-info" aria-hidden="true" />
      </button>
      {showTooltip &&
        createPortal(
          <span
            ref={contentRef}
            id={tooltipIdRef.current}
            role="tooltip"
            className="tooltip__content"
            style={{ ...CONTENT_BASE_STYLE, top: position.top, left: position.left }}
          >
            {content}
          </span>,
          getTooltipViewport().target,
        )}
    </span>
  );
};

export default Tooltip;